import { useMemo } from "react";
import type { CashierTradingAccount } from "@/api/cashier";
import { usePlatformIcon } from "../../account-list/usePlatformIcon";
import styles from "./TransactionHistoryTable.module.css";

/* The TransactionHistoryPlatformDetails component displays the trading platform icon and account ID
   for a given trading account ID. It takes in the tradingAccountId and a list of tradingAccounts
   as props. It uses the usePlatformIcon hook to get the appropriate icon for the trading platform.
   If the trading account is found, it renders a div containing the platform icon and account ID.
   If not found, it returns undefined.
   @param tradingAccountId - The ID of the trading account to display details for.
   @param tradingAccounts - An array of trading account objects to search from.
   @returns A JSX element displaying the platform icon and account ID, or undefined if not found.
*/
export const TransactionHistoryPlatformDetails = ({
  tradingAccountId,
  tradingAccounts,
}: {
  tradingAccountId?: CashierTradingAccount["id"];
  tradingAccounts?: CashierTradingAccount[];
}) => {
  const tradingAccount = useMemo(() => {
    if (!tradingAccountId || !tradingAccounts?.length) {
      return undefined;
    }
    return tradingAccounts.find(({ id }) => id === tradingAccountId);
  }, [tradingAccountId, tradingAccounts]);

  const platformIcon = usePlatformIcon(
    tradingAccount?.tradingPlatform ?? "Pepperstone"
  );

  if (!tradingAccount) {
    return undefined;
  }

  return (
    <div className={styles.platformDetails}>
      <span className={styles.iconContainer}>
        <img alt="Trading platform icon" src={platformIcon} />
      </span>
      <span>{tradingAccount.accountLoginId}</span>
    </div>
  );
};
